import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography
} from '@material-ui/core'
import moment from 'moment'
import { useContext, useEffect, useState } from 'react'
import ConfirmationModal from '../ConfirmationModal'

import { head, isArray } from 'lodash'
import { toast } from 'react-toastify'
import toastError from '../../errors/toastError'
import useCompanies from '../../hooks/useCompanies'
import api from '../../services/api'
import { CompanyModal } from '../CompanyModal'
import ModalUsers from '../ModalUsers'
import { useStyles } from './style'

import { Building2 } from 'lucide-react'
import { AuthContext } from '../../context/Auth/AuthContext'
import { CompaniesTable } from '../CompaniesTable'
import { CompaniesMetrics } from './CompaniesMetrics'
import { ButtonAddNew } from '../ButtonAddNew'

export default function CompaniesManager() {
  const classes = useStyles()
  const { list, save, update, remove } = useCompanies()

  const emptyRecord = {
    name: '',
    email: '',
    phone: '',
    planId: '',
    status: true,
    campaignsEnabled: false,
    dueDate: '',
    recurrence: ''
  }

  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false)
  const [showConfirmImpersonateDialog, setShowConfirmImpersonateDialog] =
    useState(false)
  const [showCreateUserModal, setShowCreateUserModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [records, setRecords] = useState([])
  const [financialSummary, setFinancialSummary] = useState({
    totalOpenValue: 0,
    totalOverdueValue: 0
  })
  const [financialPeriod, setFinancialPeriod] = useState('month')
  const [customStartDate, setCustomStartDate] = useState(
    moment().startOf('month').format('YYYY-MM-DD')
  )
  const [customEndDate, setCustomEndDate] = useState(
    moment().format('YYYY-MM-DD')
  )
  const [createdCompanyId, setCreatedCompanyId] = useState(null)
  const [record, setRecord] = useState(emptyRecord)

  const [showCompaniesModal, setShowCompaniesModal] = useState(false)
  const [emailInUseModalOpen, setEmailInUseModalOpen] = useState(false)
  const [emailInUseCompany, setEmailInUseCompany] = useState(null)
  const [emailInUseTitle, setEmailInUseTitle] = useState('E-mail já em uso')
  const [genericErrorModalOpen, setGenericErrorModalOpen] = useState(false)
  const [genericError, setGenericError] = useState({ title: '', message: '' })

  const { handleImpersonate, user: loggedInUser } = useContext(AuthContext)

  useEffect(() => {
    loadPlans()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadFinancialSummary()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialPeriod, customStartDate, customEndDate])

  const loadPlans = async () => {
    setLoading(true)
    try {
      const companyList = await list()
      const companiesWithUser = await Promise.all(
        companyList.map(async company => {
          try {
            const { data } = await api.get('/users/list', {
              params: { companyId: company.id }
            })
            const mainUser = isArray(data) && data.length ? head(data) : null
            return { ...company, userEmail: mainUser?.email || '' }
          } catch (e) {
            return { ...company, userEmail: '' }
          }
        })
      )
      setRecords(companiesWithUser)
    } catch (e) {
      toast.error('Não foi possível carregar a lista de registros')
    }
    setLoading(false)
  }

  const loadFinancialSummary = async () => {
    if (financialPeriod === 'custom' && (!customStartDate || !customEndDate)) {
      return
    }

    const params = { period: financialPeriod }
    if (financialPeriod === 'custom') {
      params.startDate = customStartDate
      params.endDate = customEndDate
    }

    try {
      const { data } = await api.get('/companies/financial-summary', { params })
      setFinancialSummary({
        totalOpenValue: data?.totalOpenValue || 0,
        totalOverdueValue: data?.totalOverdueValue || 0
      })
    } catch (e) {
      toast.error('Não foi possível carregar o resumo financeiro')
    }
  }

  const handleSendAccessLink = async email => {
    try {
      await api.post('/auth/access-recovery/request', { email })
      toast.success('Link de acesso enviado com sucesso!')
    } catch (err) {
      toastError(err)
    }
  }

  const handleSubmit = async data => {
    setLoading(true)

    try {
      let savedCompany = null

      if (data.id !== undefined) {
        console.log('Atualizando empresa:', data)
        savedCompany = await update(data)

        const { data: usersList } = await api.get('/users/list', {
          params: { companyId: data.id }
        })

        if (isArray(usersList) && usersList.length > 0) {
          const mainAdmin = head(usersList)

          const formatPhone = (phone, ddi) => {
            if (!phone) return null
            let clean = phone.replace(/\D/g, '')
            const cleanDDI = String(ddi).replace(/\D/g, '')
            if (
              cleanDDI === '55' &&
              clean.length === 11 &&
              clean.charAt(2) === '9'
            ) {
              clean = clean.substring(0, 2) + clean.substring(3)
            }
            return clean
          }

          const isEditingOwnUser =
            Number(mainAdmin.id) === Number(loggedInUser?.id)

          const updatedAdminUserData = {
            name: data.adminName,
            email: data.adminEmail,
            telefone: formatPhone(
              data.adminPhone,
              data.adminCountryCode || '55'
            ),
            countryCode: data.adminCountryCode
              ? String(data.adminCountryCode).replace(/\D/g, '')
              : null,
            companyId: data.id,
            queueIds: mainAdmin.queueIds || []
          }

          if (!isEditingOwnUser) {
            updatedAdminUserData.profile = mainAdmin.profile || 'admin'
          }

          if (data.adminPassword && data.adminPassword.trim() !== '') {
            updatedAdminUserData.password = data.adminPassword
          }
          await api.put(`/users/${mainAdmin.id}`, updatedAdminUserData)
        }

        toast.success('Empresa e administrador atualizados com sucesso!')
      } else {
        savedCompany = await save(data)
        toast.success('Empresa cadastrada com sucesso!')
      }

      await loadPlans()
      handleCancel()
      setShowCompaniesModal(false)

      return savedCompany
    } catch (e) {
      const errorCode = e?.response?.data?.error
      const details = e?.response?.data?.details
      const existingCompany = details?.existingCompany

      if (
        (errorCode === 'ERR_EMAIL_IN_USE' ||
          errorCode === 'ERR_COMPANY_NAME_ALREADY_EXISTS' ||
          errorCode === 'ERR_COMPANY_EMAIL_ALREADY_EXISTS') &&
        existingCompany
      ) {
        const modalTitle =
          errorCode === 'ERR_COMPANY_NAME_ALREADY_EXISTS'
            ? 'Nome já em uso'
            : errorCode === 'ERR_COMPANY_EMAIL_ALREADY_EXISTS'
              ? 'E-mail comercial já em uso'
              : 'E-mail já em uso'
        handleEmailInUse(existingCompany, modalTitle)
        return
      }

      const friendlyTitle = 'Não foi possível salvar'
      let friendlyMessage = 'Ocorreu um erro ao tentar salvar as alterações. Tente novamente.'

      if (errorCode === 'ERR_COMPANY_INVALID_NAME') {
        friendlyMessage = 'O nome informado para a empresa não é válido.'
      } else if (errorCode === 'ERR_COMPANY_NAME_ALREADY_EXISTS') {
        friendlyMessage = 'Já existe uma empresa cadastrada com este nome.'
      } else if (errorCode === 'ERR_COMPANY_EMAIL_ALREADY_EXISTS') {
        friendlyMessage = 'Já existe uma empresa cadastrada com este e-mail comercial.'
      } else if (errorCode === 'ERR_EMAIL_IN_USE') {
        friendlyMessage = 'O e-mail informado já está em uso como administrador de outra empresa.'
      } else if (errorCode === 'ERR_NO_COMPANY_FOUND') {
        friendlyMessage = 'A empresa que você está tentando editar não foi encontrada.'
      } else if (errorCode === 'ERR_NO_PERMISSION' || errorCode === 'ERR_FORBIDDEN') {
        friendlyMessage = 'Você não tem permissão para realizar esta operação.'
      } else if (details?.message) {
        friendlyMessage = details.message
      } else if (e?.response?.data?.message) {
        friendlyMessage = e.response.data.message
      } else if (e?.message) {
        friendlyMessage = e.message
      }

      setGenericError({ title: friendlyTitle, message: friendlyMessage })
      setGenericErrorModalOpen(true)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    setLoading(true)
    try {
      await remove(record.id)
      await loadPlans()
      handleCancel()
      setShowCompaniesModal(false)
      setShowConfirmDeleteDialog(false)
      toast.success('Operação realizada com sucesso!')
    } catch (e) {
      toast.error('Não foi possível realizar a operação', e)
      console.error(e)
    }
    setLoading(false)
  }

  const onImpersonate = async companyId => {
    const idToImpersonate = companyId || record.id
    if (!idToImpersonate) {
      toast.error('Não foi possível identificar a empresa para acesso.')
      return
    }
    handleImpersonate(idToImpersonate)
  }

  const handleOpenDeleteDialog = () => {
    setShowConfirmDeleteDialog(true)
  }

  const handleOpenImpersonateDialog = companyOrId => {
    if (companyOrId) {
      // Se for o objeto da linha (row) ou ID direto, atualiza o record ativo temporariamente
      const id = companyOrId.id || companyOrId
      setRecord(prev => ({ ...prev, id }))
    }
    setShowConfirmImpersonateDialog(true)
  }

  const handleCancel = () => {
    setRecord(emptyRecord)
  }

  const handleOpenCompanyModal = () => {
    handleCancel()
    setShowCompaniesModal(true)
  }

  const handleCloseCreateUserModal = () => {
    setShowCreateUserModal(false)
    setCreatedCompanyId(null)
  }

  const handleEmailInUse = (company, title) => {
    setEmailInUseCompany(company)
    setEmailInUseTitle(
      title || 'E-mail já em uso'
    )
    setEmailInUseModalOpen(true)
  }

  const handleCloseEmailInUseModal = () => {
    setEmailInUseModalOpen(false)
    setEmailInUseCompany(null)
    setEmailInUseTitle('E-mail já em uso')
  }

  const handleCloseGenericErrorModal = () => {
    setGenericErrorModalOpen(false)
    setGenericError({ title: '', message: '' })
  }

  const handleSelect = async data => {
    setLoading(true)

    let campaignsEnabled = false
    const setting = data.settings?.find(
      s => s.key.indexOf('campaignsEnabled') > -1
    )
    if (setting) {
      campaignsEnabled = setting.value === 'true' || setting.value === 'enabled'
    }

    const updatedRecord = {
      id: data.id,
      name: data.name || '',
      phone: data.phone || '',
      email: data.email || '',
      planId: data.planId || '',
      status: data.status !== false,
      campaignsEnabled,
      dueDate: data.dueDate || '',
      recurrence: data.recurrence || '',
      resposavel: data.resposavel || '',
      adminName: '',
      adminEmail: '',
      adminPhone: ''
    }

    try {
      const response = await api.get('/users/list', {
        params: { companyId: data.id }
      })

      if (isArray(response.data) && response.data.length > 0) {
        const mainAdmin = head(response.data)
        updatedRecord.adminName = mainAdmin.name || ''
        updatedRecord.adminEmail = mainAdmin.email || ''
        updatedRecord.adminPhone = mainAdmin.telefone || mainAdmin.phone || ''
        updatedRecord.adminCountryCode = mainAdmin.countryCode || '55'
      }
    } catch (e) {
      console.error('Não foi possível carregar os dados do administrador:', e)
      toast.error('Aviso: Falha ao carregar os dados do usuário administrador.')
    } finally {
      setRecord(updatedRecord)
      setShowCompaniesModal(true)
      setLoading(false)
    }
  }

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <Grid spacing={2} container>
        <Grid xs={12} item className={classes.containerHeader}>
          <Grid xs={6} className={classes.contentHeader}>
            <div className={classes.contentIcon}>
              <Building2 className={classes.icon} />
            </div>
            <div className={classes.titlePage}>
              <Typography variant="h5"> Empresas Cadastradas</Typography>
              <span>
                Gerencie as empresas, planos, vencimentos e módulos ativos no
                sistema.
              </span>
            </div>
          </Grid>
          <ButtonAddNew
            variant="contained"
            className={classes.buttonNewQuickMessage}
            onClick={handleOpenCompanyModal}
            startIcon={<Building2 size={20} />}
            label="Nova Empresa"
          />
        </Grid>
        <Grid xs={12} item>
          <CompaniesMetrics
            records={records}
            totalOpenValue={financialSummary.totalOpenValue}
            totalOverdueValue={financialSummary.totalOverdueValue}
            period={financialPeriod}
            onPeriodChange={e => setFinancialPeriod(e.target.value)}
            customStartDate={customStartDate}
            onCustomStartDateChange={e => setCustomStartDate(e.target.value)}
            customEndDate={customEndDate}
            onCustomEndDateChange={e => setCustomEndDate(e.target.value)}
          />
        </Grid>
        <Grid xs={12} item>
          <CompaniesTable
            records={records}
            onSelect={handleSelect}
            // onSendAccessLink={handleSendAccessLink}
            onImpersonate={handleOpenImpersonateDialog}
          />
        </Grid>
      </Grid>

      <CompanyModal
        open={showCompaniesModal}
        onClose={() => setShowCompaniesModal(false)}
        initialValue={record}
        onDelete={handleOpenDeleteDialog}
        onSubmit={handleSubmit}
        onEmailInUse={handleEmailInUse}
        onCancel={handleCancel}
        loading={loading}
        onSendAccessLink={handleSendAccessLink}
        submitLabel={record.id !== undefined ? 'Salvar' : 'Próximo'}
      />

      <ModalUsers
        open={showCreateUserModal}
        onClose={handleCloseCreateUserModal}
        companyId={createdCompanyId}
      />

      <ConfirmationModal
        title="Exclusão de Registro"
        open={showConfirmDeleteDialog}
        onClose={() => setShowConfirmDeleteDialog(false)}
        onConfirm={() => handleDelete()}
      >
        Deseja realmente excluir esse registro?
      </ConfirmationModal>
      <ConfirmationModal
        title="Acessar como"
        open={showConfirmImpersonateDialog}
        onClose={() => setShowConfirmImpersonateDialog(false)}
        onConfirm={() => onImpersonate(record.id)}
      >
        Deseja acessar o sistema como esta empresa?
      </ConfirmationModal>

      <Dialog
        open={emailInUseModalOpen}
        onClose={handleCloseEmailInUseModal}
        maxWidth="sm"
        fullWidth
        aria-labelledby="email-in-use-title"
      >
        <DialogTitle id="email-in-use-title">{emailInUseTitle}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            {emailInUseTitle === 'Nome já em uso'
              ? 'O nome informado já está sendo utilizado por outra empresa.'
              : emailInUseTitle === 'E-mail comercial já em uso'
                ? 'O e-mail comercial informado já está sendo utilizado por outra empresa.'
                : 'O e-mail informado já está sendo utilizado como administrador de outra empresa.'}
          </Typography>
          {emailInUseCompany && (
            <Paper variant="outlined" style={{ padding: 16, marginTop: 16 }}>
              <Typography variant="subtitle2" gutterBottom>
                Empresa vinculada:
              </Typography>
              <Typography variant="body2">
                <strong>ID:</strong> {emailInUseCompany.id}
              </Typography>
              <Typography variant="body2">
                <strong>Nome:</strong> {emailInUseCompany.name}
              </Typography>
              {emailInUseCompany.email && (
                <Typography variant="body2">
                  <strong>E-mail:</strong> {emailInUseCompany.email}
                </Typography>
              )}
              {emailInUseCompany.phone && (
                <Typography variant="body2">
                  <strong>Telefone:</strong> {emailInUseCompany.phone}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Status:</strong>{' '}
                {emailInUseCompany.status ? 'Ativo' : 'Inativo'}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseEmailInUseModal}
            color="primary"
            variant="contained"
          >
            Entendi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={genericErrorModalOpen}
        onClose={handleCloseGenericErrorModal}
        maxWidth="sm"
        fullWidth
        aria-labelledby="generic-error-title"
      >
        <DialogTitle id="generic-error-title">
          {genericError.title || 'Atenção'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            {genericError.message || 'Ocorreu um erro inesperado.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseGenericErrorModal}
            color="primary"
            variant="contained"
          >
            Entendi
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}
